import { Registers, Common, Pagination } from '@factorin/domain';

import { get, patch, post, withUrl, list, Request, ItemRequest } from '../rest';


export type ChangeRegisterStageRequest = {
    Stage: string;
}

export interface CessionsFinanceRequest {
    cessionsUids: string[];
    finance: boolean;
}

export interface CessionsFinanceResponse {
    RegisterFinancedUid?: string;
    RegisterNonFinancedUid?: string;
}

type Download =
    Request.DownloadProgress &
    Request.Cancel &
    ItemRequest;

type DownloadExcel = Download & {
    registerType: 'r1' | 'r2';
}

export interface RegisterListRequest extends Pagination.Request {
    clientORProviderUid?: string;
    clientORProviderINN?: string;
    debtorORBuyerUid?: string;
    debtorORBuyerINN?: string;
    factorUid?: string;
    factorINN?: string;
    number?: string;
    isSignAllowed?: boolean;
    isClientSignAllowed?: boolean;
    isFactorSignAllowed?: boolean;
    createDateRange?: Common.DateRange;
    clientORProviderSignDateRange?: Common.DateRange;
    amount?: Common.DecimalRange;
    cessionAmount?: Common.DecimalRange;
    registerStatusName?: string;
    registerStatusCode?: string;
    registerStageName?: string;
    registerStageCode?: string;
    registerTemplateCode?: string;
    supplyAgreementDateRange?: Common.DateRange;
    factoringAgreementDateRange?: Common.DateRange;
    registerStatusMask?: number;
    registerStageMask?: number;
    registerTemplateMask?: number;
    registerStartDate?: Date;
    registerEndDate?: Date;
    parentUid?: string;
}

type ValidateBySource = {
    uid: string;
    isOk: boolean;
};

type ColumnsFields = {
    Field: string;
    ColumnText: string;
}

interface DownloadExcelRequest extends RegisterListRequest {
    columnItems: ColumnsFields[];
}

interface NoticeRequest {
    RegisterFinancedUid: string;
    AccountUid: string;
}

interface NoticeResponse {
    RegisterNoticeOfCessUid: string;
}

const registersPart = withUrl('registers');

export const registers = {
    ...list<Registers.RegisterDetails, RegisterListRequest, Registers.Register>(registersPart()),

    downloadExcel: get<DownloadExcel>(
        ({ id, registerType, ...request }) => registersPart({
            path: `${id}/${registerType}downloadexcel`,
            responseType: 'blob',
            ...request,
        }),
    ),

    download: get<Download>(
        ({ id, ...request }) => registersPart({
            path: `${id}/zip`,
            responseType: 'blob',
            ...request,
        }),
    ),

    acknowledge: patch<ItemRequest, void>(
        ({ id }) => registersPart({
            path: `${id}`,
            body: {
                Stage: 'acknowledge',
            },
        }),
    ),

    validateBySource: patch<ValidateBySource, void>(
        ({ uid, isOk }) => registersPart({
            path: `${uid}/validatebysource`,
            body: isOk,
        }),
    ),

    changeStage: patch<ItemRequest & Request.Body<ChangeRegisterStageRequest>, void>(
        ({ id, body }) => registersPart({
            path: `${id}`,
            body,
        }),
    ),

    finance: patch<ItemRequest & CessionsFinanceRequest, CessionsFinanceResponse>(
        ({ id, cessionsUids, finance }) => registersPart({
            path: `${id}/finance`,
            body: {
                Finance: finance,
                CessionsUids: cessionsUids,
            },
        }),
    ),

    downloadRegistersInExcel: post<Request.Body<DownloadExcelRequest>>(
        ({ body, ...request }) => registersPart({
            path: 'excel/download',
            responseType: 'blob',
            body,
            ...request,
        }),
    ),

    notice: post<Request.Body<NoticeRequest>, NoticeResponse>(
        ({ body }) => registersPart({
            path: 'notice',
            body,
        }),
    ),
};
